import { CSSProperties, useState } from 'react'
import useEventListener from '@use-it/event-listener'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import './styles.css'
import { LoginInput } from '../../../components/InputLogin'
import { Loading } from '../../../components/Loading/Index'
import { useAuth } from '../../../hooks/useAuth'
import { ButtonOnlyTitle } from '../../../components/Buttons/ButtonOnlyTitle/Index'
import { AlertModal } from '../../../components/Modal/Alert/Index'
import { SimpleButtonClose } from '../../../components/Modal/Contents/SimpleButtonClose/Index'
import Constants from '../../../constants/Index'

const schemaLogin = Yup.object().shape({
  phone: Yup.string()
    .trim('Diga seu Telefone')
    .min(15, '*Telefone informado inválido!')
    .max(15, '*Telefone informado inválido!')
    .required('*Campo obrigatório não informado.'),
  password: Yup.string()
    .trim('Diga sua Senha')
    .required('*Campo obrigatório não informado.'),
})


export type UserProps = {
  phone: string
  password: string
}

export function Login() {
  const { signIn } = useAuth()

  let [searchParams] = useSearchParams()

  let redirectURL = searchParams.get('redirect')

  const navigate = useNavigate()

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<UserProps>({
    resolver: yupResolver(schemaLogin),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      phone: '',
      password: ''
    }
  })

  // Isso diz se o usuário é válido ou não
  const [invalidUser, setInvalidUser] = useState<boolean>(false)

  // Isso diz a página quando mostrar o loading
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [errorMessage, setErrorMessage] = useState<any>('')

  const onSubmit = async (formData: UserProps) => {
    // ao tentar logar redefinimos os erros para null
    setInvalidUser(false)

    try {
      // Remove caracteres não numéricos do telefone
      const cleanPhone = formData.phone.replace(/[^0-9]+/g, '')
      const dataToSubmit = { ...formData, phone: cleanPhone }

      // Isso abre o loading
      setIsLoading(true)
      // isso chama a função de sigin no contexto
      const response = await signIn(dataToSubmit)
      // Fecha loading
      setIsLoading(false)
      setErrorMessage(response.message)

      // Se a APi retorna 401 as credenciais inseridas estão incorretas
      if (response.statusCode === 400) {
        setInvalidUser(true)
      } else {
        // Só salva o token e navega se o login foi bem-sucedido
        if (response.token) {
          localStorage.setItem('token', response.token)
        }

        // Se o usuário nao confirmou e-mail redirecionamos pra /bemvindo
        if (response.changePassword) {
          navigate(Constants.RESET_PASSWORD)
        } else {
          //Se o usuário vier por redirecionamento voltamos pra tela anterior
          if (redirectURL) {
            navigate(redirectURL)
          } else {
            navigate(Constants.PADDOCKS_MAP)
          }
        }
      }
    } catch (err) {
      setIsLoading(false)
      console.error('Erro no login:', err)
    }
  }

  useEventListener('keyup', async (event: KeyboardEvent) => {
    const { key } = event
    if (key === 'Enter') handleSubmit(onSubmit)()
  })

  const contentStyleAlert = {
    width: '90%',
    maxWidth: '440px',
    height: '250px',
    borderRadius: '20px',
    padding: '20px',
    boxSizing: 'border-box',
  } as CSSProperties

  const [visibleAlert, setVisibleAlert] = useState<boolean>(false)

  return (
    <section className="login__container">
      <Loading visible={isLoading} />
      <h1 className="login__container--title">Faça seu login.</h1>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="login__form--input">
          <LoginInput
            variant="phone"
            control={control}
            label="Telefone"
            id="phone"
            error={errors.phone}
          />
        </div>

        <div className="login__form--input">
          <LoginInput
            variant="password"
            control={control}
            label="Senha"
            id="password"
            error={errors.password}
            maxLength={32}
          />
        </div>

        {/* Se as credenciais inseridas não forem válidas mostramos a msg de erro */}
        {invalidUser && (
          <strong className="login__container--error" id="erro">
            {errorMessage?.msg || errorMessage || 'Credenciais inválidas'}
          </strong>
        )}

        <button
          type="button"
          className="login__form--forgot"
          onClick={() => setVisibleAlert(true)}
          id="esqueceuSuaSenha"
        >
          Esqueceu sua senha?
        </button>

        <AlertModal
          contentStyle={contentStyleAlert}
          visible={visibleAlert}
          animation="slideUp"
        >
          <SimpleButtonClose onClick={() => setVisibleAlert(false)} />
          <section className="alert-modal--section-forget">
            <h2 id="titulo">Esqueceu sua senha?</h2>
            <p id="mensagem">
              Entre em contato com o seu Administrador da fazenda.
            </p>
            <div className="alert-modal--section-buttons-voltar ">
              <button
                onClick={() => setVisibleAlert(false)}
                id="bt_voltar"
                type="button"
              >
                <strong>VOLTAR</strong>
              </button>
            </div>
          </section>
        </AlertModal>

        <div className="login__form--button-container">
          <ButtonOnlyTitle
            title="Continuar"
            theme="success"
            onClick={handleSubmit(onSubmit)}
            id="bt_continuar"
          />
        </div>
      </form>
    </section>
  )
}
